import type { SupabaseClient } from '@supabase/supabase-js';

import {
  AUTH_BRIDGE_VERSION,
  type EmbedBridgeEventHandlers,
  type EmbedBridgeMessage,
} from './types';

import { logger } from '@/lib/logger/browser';

declare global {
  interface Window {
    __KK_APP_CONTAINER__?: boolean;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

// Comma-separated origins allowed to frame the site and supply a session, e.g. http://localhost:8081
const PARENT_ORIGINS = (process.env.NEXT_PUBLIC_EMBED_PARENT_ORIGINS ?? '')
  .split(',')
  .filter(Boolean);

const parsePayload = (payload: unknown): EmbedBridgeMessage | null => {
  if (typeof payload === 'object') {
    return payload as EmbedBridgeMessage;
  }

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }

  return null;
};

export function isAppContainer() {
  if (typeof window === 'undefined') return false;

  const rawValue = process.env.NEXT_PUBLIC_APP_CONTAINER ?? window.__KK_APP_CONTAINER__;
  const normalized = typeof rawValue === 'string' ? rawValue.toLowerCase() : rawValue;

  window.__KK_APP_CONTAINER__ = Boolean(
    normalized === true || normalized === 'true' || normalized === '1',
  );

  return window.__KK_APP_CONTAINER__;
}

export function initEmbedBridge(supabase: SupabaseClient, handlers: EmbedBridgeEventHandlers = {}) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const appContainer = isAppContainer();
  const isWebView = !!window.ReactNativeWebView;
  const parentWindow = window.parent && window.parent !== window ? window.parent : null;

  const parentOrigin =
    window.location.ancestorOrigins?.[0] ??
    (document.referrer ? new URL(document.referrer).origin : '');

  const allowedOrigin =
    !PARENT_ORIGINS.length || (parentOrigin && PARENT_ORIGINS.includes(parentOrigin)) || isWebView;

  if (!allowedOrigin) {
    return () => {};
  }

  logger.info(
    {
      window: typeof window === 'undefined',
      parentWindow: parentWindow === null,
      isWebView,
      parentOrigin,
      allowedOrigin,
      isAppContainer: appContainer,
    },
    '[embed] initializing embed bridge',
  );

  const post = (message: EmbedBridgeMessage) => {
    const payload = typeof message === 'string' ? message : JSON.stringify(message);

    if (parentWindow) {
      parentWindow.postMessage(payload, parentOrigin || '*');
      return;
    }

    if (isWebView) {
      window.ReactNativeWebView?.postMessage(payload);
    }
  };

  const pick = (session: { access_token: string; refresh_token: string; expires_at?: number }) => ({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_at: session.expires_at,
  });

  const handleMessage = async (event: MessageEvent) => {
    logger.debug({ event }, '[embed] message from');

    const nextMessage = parsePayload(event.data);

    logger.debug({ nextMessage }, '[embed] parsed message');

    if (!nextMessage) {
      return;
    }

    const trustedSource =
      !parentWindow || (event.source === parentWindow && event.origin === parentOrigin);

    if (!trustedSource) {
      return;
    }

    if (nextMessage?.type === 'kk:auth:logout') {
      logger.info('[embed] logout requested');
      await supabase.auth.signOut();
      handlers.onLoggedOut?.(appContainer);
      post({ type: 'kk:auth:logged-out', appContainer, version: AUTH_BRIDGE_VERSION });
      return;
    }

    if (nextMessage?.type !== 'kk:auth:session') {
      return;
    }

    const { access_token, refresh_token } = nextMessage.session ?? {};

    if (!access_token || !refresh_token) {
      return;
    }

    // The browser client writes the @supabase/ssr cookies itself, so no cookie format is needed here.
    const { error, data } = await supabase.auth.setSession({ access_token, refresh_token });

    logger.info({ error, data }, '[embed] setSession result');

    if (!error) {
      window.location.reload();
    } else {
      logger.error(error);
    }
  };

  const subscription = supabase.auth.onAuthStateChange((event, session) => {
    if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
      const nextSession = pick(session);
      handlers.onSessionUpdated?.({ session: nextSession, appContainer });
      post({
        type: 'kk:auth:session:updated',
        session: nextSession,
        appContainer,
        version: AUTH_BRIDGE_VERSION,
      });
    }

    if (event === 'SIGNED_OUT') {
      handlers.onLoggedOut?.(appContainer);
      post({ type: 'kk:auth:logged-out', appContainer, version: AUTH_BRIDGE_VERSION });
    }
  });

  window.addEventListener('message', handleMessage);

  supabase.auth.getSession().then(({ data }) => {
    const authenticated = !!data.session;
    handlers.onAuthReady?.({ authenticated, appContainer });
    post({
      type: 'kk:auth:ready',
      authenticated,
      appContainer,
      version: AUTH_BRIDGE_VERSION,
    });
  });

  return () => {
    window.removeEventListener('message', handleMessage);
    subscription.data.subscription.unsubscribe();
  };
}
